import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import {
  competenciaDasDatas,
  esquecerCompetencia,
  guardarCompetencia,
  guardarRecorte,
  recorteGuardado,
  recortePadrao,
  type RecorteDoCabecalho,
} from './header-store';

/** A forma vive no store, que é quem a confere ao ler do storage. */
type FiltrosHeader = RecorteDoCabecalho;

interface HeaderContextData {
  filtros: FiltrosHeader;
  setEmpresa: (empresaId: string | null) => void;
  setEmpresaCnpjs: (empresaCnpjs: string | null) => void;
  setDataInicio: (data: string | null) => void;
  setDataFim: (data: string | null) => void;
  setFiltros: (filtros: Partial<FiltrosHeader>) => void;
  limparFiltros: () => void;
  /** Tira a empresa e o "já buscado", e mantém a competência — para quem troca de módulo. */
  limparEmpresa: () => void;
  /** Registra que houve um "Buscar" — é o que faz as outras telas abrirem já buscadas. */
  marcarAplicado: () => void;
}

const HeaderContext = createContext<HeaderContextData | undefined>(undefined);

const CAMPOS_DA_COMPETENCIA = [
  'dataInicio',
  'dataFim',
  'periodoModo',
  'periodoMes',
  'periodoAno',
] as const satisfies readonly (keyof FiltrosHeader)[];

function mexeNaCompetencia(parcial: Partial<FiltrosHeader>): boolean {
  return CAMPOS_DA_COMPETENCIA.some(campo => campo in parcial);
}

export function HeaderProvider({ children }: { children: ReactNode }) {
  /*
    Decisão 11 da SPEC-017: o recorte escolhido uma vez vale em todas as telas e CONTINUA AO
    RECARREGAR. Antes ele vivia só na memória da página, então um F5 devolvia a pessoa aos
    "últimos 30 dias" e sem empresa — e cada tela pedia tudo de novo.

    A leitura é no inicializador do `useState`, não num efeito: num efeito, o primeiro render
    aconteceria com o padrão e as telas poderiam disparar consulta com o recorte errado antes
    de o certo chegar.
  */
  const [filtros, setFiltrosState] = useState<FiltrosHeader>(recorteGuardado);

  /*
    Decisão 13: a competência ESCOLHIDA vale também em outra aba e no próximo acesso. Só a
    escolha se grava — quem nunca escolheu segue no mês corrente, e não fica preso no mês em
    que abriu a tela pela primeira vez. Por isso a gravação é marcada por quem muda a
    competência, e não deduzida de o valor ter mudado.
  */
  const competenciaPendente = useRef<'guardar' | 'esquecer' | null>(null);

  useEffect(() => {
    guardarRecorte(filtros);

    const pendente = competenciaPendente.current;
    competenciaPendente.current = null;
    if (pendente === 'guardar') guardarCompetencia(filtros);
    if (pendente === 'esquecer') esquecerCompetencia();
  }, [filtros]);

  const setEmpresa = (empresaId: string | null) => {
    setFiltrosState(prev => ({ ...prev, empresaId, aplicado: false }));
  };

  const setEmpresaCnpjs = (empresaCnpjs: string | null) => {
    setFiltrosState(prev => ({ ...prev, empresaCnpjs, aplicado: false }));
  };

  const setDataInicio = (dataInicio: string | null) => {
    competenciaPendente.current = 'guardar';
    setFiltrosState(prev => ({
      ...prev,
      ...competenciaDasDatas(dataInicio, prev.dataFim),
      aplicado: false,
    }));
  };

  const setDataFim = (dataFim: string | null) => {
    competenciaPendente.current = 'guardar';
    setFiltrosState(prev => ({
      ...prev,
      ...competenciaDasDatas(prev.dataInicio, dataFim),
      aplicado: false,
    }));
  };

  /*
    Mexer no recorte DESFAZ o "aplicado". Sem isso, trocar o período e navegar faria a tela
    seguinte abrir buscando — com o recorte novo que ninguém mandou buscar ainda, ou pior,
    mostrando o resultado do anterior como se fosse dele.
  */
  const setFiltros = (novosFiltros: Partial<FiltrosHeader>) => {
    if (mexeNaCompetencia(novosFiltros)) competenciaPendente.current = 'guardar';
    setFiltrosState(prev => ({ ...prev, ...novosFiltros, aplicado: false }));
  };

  const marcarAplicado = useCallback(() => {
    setFiltrosState(prev => (prev.aplicado ? prev : { ...prev, aplicado: true }));
  }, []);

  const limparFiltros = useCallback(() => {
    competenciaPendente.current = 'esquecer';
    setFiltrosState(recortePadrao());
  }, []);

  const limparEmpresa = useCallback(() => {
    setFiltrosState(prev => ({ ...prev, empresaId: null, empresaCnpjs: null, aplicado: false }));
  }, []);

  return (
    <HeaderContext.Provider
      value={{
        filtros,
        setEmpresa,
        setEmpresaCnpjs,
        setDataInicio,
        setDataFim,
        setFiltros,
        limparFiltros,
        limparEmpresa,
        marcarAplicado,
      }}
    >
      {children}
    </HeaderContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useHeader() {
  const context = useContext(HeaderContext);
  if (!context) {
    throw new Error('useHeader deve ser usado dentro de HeaderProvider');
  }
  return context;
}
